<template>
  <div class="chat-agent-card">
    <!-- 智能体头像和状态 -->
    <div class="card-avatar">
      <head-image
        :size="48"
        :name="agent.agentName"
        :url="agent.avatarUrl"
        :style="{ borderRadius: '50%', border: '2px solid white' }"
      ></head-image>

      <!-- 状态指示器 -->
      <div class="status-indicator" :class="statusClass" :title="statusText"></div>
    </div>

    <!-- 智能体信息 -->
    <div class="card-info">
      <div class="card-header">
        <h4 class="agent-name">{{ agent.agentName }}</h4>
        <div class="agent-badges">
          <el-tag v-if="isOnline" size="mini" type="warning" effect="plain">在线</el-tag>
        </div>
      </div>

      <p class="agent-description">
        {{ agent.description || '暂无描述' }}
      </p>
      
      <div class="agent-meta">
        <div class="meta-item">
          <i class="el-icon-cpu"></i>
          <span>{{ modeText }}</span>
        </div>
        <div class="meta-item">
          <i class="el-icon-connection"></i>
          <span>{{ serviceStatusText }}</span>
        </div>
      </div>

      <!-- 技能、MCP、工作流标签 -->
      <div class="agent-tags" v-if="hasSkillsOrMcpOrWorkflow">
        <!-- 技能标签 -->
        <div class="feature-tag skills-tag" v-if="agent.skills && agent.skills.length > 0">
          <i class="el-icon-lightning"></i>
          <span class="tag-label">技能</span>
          <span class="tag-badge">{{ agent.skills.length }}</span>
        </div>

        <!-- MCP标签 -->
        <div class="feature-tag mcp-tag" v-if="agent.mcp && agent.mcp.servers && agent.mcp.servers.length > 0">
          <i class="el-icon-link"></i>
          <span class="tag-label">MCP</span>
          <span class="tag-badge">{{ agent.mcp.servers.length }}</span>
        </div>

        <!-- 工作流标签 -->
        <div class="feature-tag workflow-tag" v-if="hasWorkflow">
          <i class="el-icon-share"></i>
          <span class="tag-label">工作流</span>
          <span class="tag-badge">{{ workflowCount }}</span>
        </div>
      </div>

      <!-- 能力标签 -->
      <div class="agent-capabilities" v-if="displayCapabilities.length > 0">
        <el-tag
          v-for="cap in displayCapabilities"
          :key="cap"
          size="mini"
          effect="plain"
          type="info"
        >
          {{ cap }}
        </el-tag>
      </div>
    </div>
    
    <!-- 操作按钮 -->
    <div class="card-actions" v-if="showActions">
      <el-button 
        size="small" 
        type="primary"
        :disabled="!isAvailable"
        @click="$emit('start-chat', agent)"
        block
      >
        开始对话
      </el-button>
    </div>
  </div>
</template>

<script>
import HeadImage from "../common/HeadImage.vue";
import agentCardMixin from "../../mixins/agentCardMixin.js";

export default {
  name: "ChatAgentCard",
  components: { HeadImage },
  mixins: [agentCardMixin],
  computed: {
    // 是否有技能、MCP 或工作流
    hasSkillsOrMcpOrWorkflow() {
      return (
        (this.agent.skills && this.agent.skills.length > 0) ||
        (this.agent.mcp && this.agent.mcp.servers && this.agent.mcp.servers.length > 0) ||
        this.hasWorkflow
      );
    },
    // 是否有工作流
    hasWorkflow() {
      if (!this.agent.workflow) return false;
      // 检查是否启用或者有工作流节点/workflows
      return (
        this.agent.workflow.enabled ||
        (this.agent.workflow.workflows && this.agent.workflow.workflows.length > 0) ||
        (this.agent.workflow.nodes && this.agent.workflow.nodes.length > 0)
      );
    },
    // 工作流数量
    workflowCount() {
      if (!this.agent.workflow) return 0;
      // 优先使用 workflows 数组的长度,其次使用 nodes 数组的长度
      if (this.agent.workflow.workflows && this.agent.workflow.workflows.length > 0) {
        return this.agent.workflow.workflows.length;
      }
      if (this.agent.workflow.nodes && this.agent.workflow.nodes.length > 0) {
        return this.agent.workflow.nodes.length;
      }
      return this.agent.workflow.enabled ? 1 : 0;
    }
  }
}
</script>

<style lang="scss" scoped>
.chat-agent-card {
  background: white;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  // 禁用文本选择，避免hover时的选中效果
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  
  .card-avatar {
    position: relative;
    width: 48px;
    height: 48px;
    margin: 0 auto;
    
    .status-indicator {
      position: absolute;
      bottom: 0;
      right: 0;
      width: 14px;
      height: 14px;
      border-radius: 50%;
      border: 2px solid white;
      
      &.status-online {
        background: #52c41a;
        animation: pulse 2s infinite;
      }
      
      &.status-offline {
        background: #d9d9d9;
      }
      
      &.status-inactive {
        background: #ff4d4f;
      }
    }
  }
  
  .card-info {
    flex: 1;
    
    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;
      
      .agent-name {
        margin: 0;
        font-size: 15px;
        font-weight: 600;
        color: #303133;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      
      .agent-badges {
        display: flex;
        gap: 4px;
      }
    }
    
    .agent-description {
      color: #606266;
      font-size: 13px;
      line-height: 1.5;
      margin: 0 0 12px 0;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
    
    .agent-meta {
      display: flex;
      gap: 12px;
      margin-bottom: 8px;

      .meta-item {
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 12px;
        color: #909399;

        i {
          font-size: 14px;
        }
      }
    }

    // 技能、MCP、工作流标签
    .agent-tags {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-top: 8px;
      margin-bottom: 8px;

      .feature-tag {
        display: flex;
        align-items: center;
        gap: 4px;
        padding: 4px 8px;
        border-radius: 12px;
        font-size: 12px;
        font-weight: 500;
        transition: all 0.2s ease;

        i {
          font-size: 12px;
        }

        .tag-label {
          line-height: 1;
        }

        .tag-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 18px;
          height: 18px;
          padding: 0 6px;
          border-radius: 9px;
          font-size: 10px;
          font-weight: 600;
          color: white;
          line-height: 1;
        }

        // 技能标签 - 蓝色
        &.skills-tag {
          background-color: rgba(64, 158, 255, 0.12);
          color: #409EFF;

          .tag-badge {
            background-color: #409EFF;
          }

          &:hover {
            background-color: rgba(64, 158, 255, 0.2);
          }
        }

        // MCP标签 - 橙色
        &.mcp-tag {
          background-color: rgba(255, 149, 0, 0.12);
          color: #FF9500;

          .tag-badge {
            background-color: #FF9500;
          }

          &:hover {
            background-color: rgba(255, 149, 0, 0.2);
          }
        }

        // 工作流标签 - 绿色
        &.workflow-tag {
          background-color: rgba(103, 194, 58, 0.12);
          color: #67C23A;

          .tag-badge {
            background-color: #67C23A;
          }

          &:hover {
            background-color: rgba(103, 194, 58, 0.2);
          }
        }
      }
    }

    .agent-capabilities {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
    }
  }
  
  .card-actions {
    .el-button {
      width: 100%;
      // 防止自动聚焦
      &:focus {
        outline: none;
      }
    }
  }
  
  // 防止描述文本被选中
  .agent-description {
    cursor: default;
  }
  
  // 确保所有文本元素不可选择
  * {
    user-select: none;
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}
</style>